import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner } from '@/icons';
import Select from 'react-select';
import { useAuthContext } from '../contexts/AuthContext';
import { CaseReceivedSummary } from '../types/caseApi';
import { isJudicialTeamType } from '../utils/caseTeamType';
import { useSingleFlight } from '../hooks/useSingleFlight';

interface AcceptCaseModalProps {
    isOpen: boolean;
    receivedCase: CaseReceivedSummary | null;
    onClose: () => void;
    /** `requestId` is one per opening of the modal (see useSingleFlight); a repeat returns the same case. */
    onAccept: (data: {
        representedParty?: 'partyA' | 'partyB';
        attorneyId?: string;
        caseNote: string;
    }, requestId: string) => void | Promise<void>;
    accepting: boolean;
}

export const AcceptCaseModal: React.FC<AcceptCaseModalProps> = ({
    isOpen,
    receivedCase,
    onClose,
    onAccept,
    accepting,
}) => {
    const theme = useTheme();
    const { user: currentUser } = useAuthContext();
    const isJudicial = isJudicialTeamType(currentUser?.team?.teamType);
    const [representedParty, setRepresentedParty] = useState<'partyA' | 'partyB'>('partyA');
    const [selectedAttorney, setSelectedAttorney] = useState<{ value: string; label: string } | null>(null);
    const [caseNote, setCaseNote] = useState('');

    // Get party names from received case
    const partyAName = useMemo(() => {
        if (!receivedCase) return 'Party A';
        return receivedCase.afn && receivedCase.aln
            ? `${receivedCase.afn} ${receivedCase.aln}`
            : 'Party A';
    }, [receivedCase]);

    const partyBName = useMemo(() => {
        if (!receivedCase) return 'Party B';
        return receivedCase.bfn && receivedCase.bln
            ? `${receivedCase.bfn} ${receivedCase.bln}`
            : 'Party B';
    }, [receivedCase]);

    // Build attorney options from current user's team
    const attorneyOptions = useMemo(() => {
        const options: { value: string; label: string }[] = [];

        if (!currentUser?.team?.users) return options;

        // Filter for attorneys that are not inactive (active or pending T&C both qualify)
        const attorneys = currentUser.team.users.filter(
            user => user.businessRole?.toLowerCase() === 'attorney' && user.status?.toLowerCase() !== 'inactive'
        );

        attorneys.forEach(attorney => {
            options.push({
                value: attorney.userId,
                label: attorney.fullName || `${attorney.firstName} ${attorney.lastName}`.trim()
            });
        });

        return options;
    }, [currentUser]);

    // Set default attorney when modal opens
    useEffect(() => {
        if (isOpen && attorneyOptions.length > 0) {
            // Default to current user if they are an attorney
            if (currentUser?.businessRole?.toLowerCase() === 'attorney' && currentUser?.userId) {
                const currentUserOption = attorneyOptions.find(opt => opt.value === currentUser.userId);
                if (currentUserOption) {
                    setSelectedAttorney(currentUserOption);
                } else {
                    // Otherwise default to first attorney
                    setSelectedAttorney(attorneyOptions[0]);
                }
            } else {
                // Default to first attorney
                setSelectedAttorney(attorneyOptions[0]);
            }
        } else if (!isOpen) {
            // Reset when modal closes
            setRepresentedParty('partyA');
            setSelectedAttorney(null);
            setCaseNote('');
        }
    }, [isOpen, attorneyOptions, currentUser]);

    // Generate default case note
    useEffect(() => {
        if (isOpen && receivedCase && !caseNote) {
            const senderName = receivedCase.rcvFrom?.userName || 'Unknown';
            const senderTeam = receivedCase.rcvFrom?.teamName || '';
            const senderInfo = senderTeam ? `${senderName} (${senderTeam})` : senderName;
            const receivedDate = receivedCase.rcvAt ? new Date(receivedCase.rcvAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            }) : new Date().toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
            setCaseNote(`Copy #1, sent by ${senderInfo} on ${receivedDate}`);
        }
    }, [isOpen, receivedCase, caseNote]);

    // One accept at a time: blocks a double click or a repeated Enter from the first click until
    // the request settles. Each opening of the modal is a new user action.
    const acceptFlight = useSingleFlight();
    const { resetRequestId } = acceptFlight;
    useEffect(() => { if (isOpen) resetRequestId(); }, [isOpen, receivedCase?.csid, resetRequestId]);
    const isAccepting = accepting || acceptFlight.inFlight;

    const handleAccept = () => {
        if (isAccepting || (!isJudicial && !selectedAttorney)) {
            return;
        }
        const data = {
            representedParty: isJudicial ? undefined : representedParty,
            attorneyId: isJudicial ? undefined : selectedAttorney?.value,
            caseNote,
        };
        // The caller reports a failure; a rejection only keeps the request id for a retry.
        void acceptFlight.run((requestId) => Promise.resolve(onAccept(data, requestId))).catch(() => {});
    };

    if (!isOpen || !receivedCase) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        }}>
            <div style={{
                backgroundColor: (theme.colors.background as any)?.primary || '#ffffff',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                }}>
                    <h2 style={{
                        margin: 0,
                        fontSize: theme.typography?.fontSize?.xl || '24px',
                        fontWeight: 'bold',
                        color: theme.colors.primary || '#6B46C1',
                        textTransform: 'uppercase',
                    }}>
                        Accept Case
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '20px',
                            color: theme.colors.text?.secondary || '#6b7280',
                            padding: '4px 8px',
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <p style={{
                        fontSize: theme.typography?.fontSize?.sm || '14px',
                        color: theme.colors.text?.primary || '#000000',
                        marginBottom: '12px',
                        lineHeight: '1.5',
                    }}>
                        When you accept this case, we'll make a copy in your cases and leave the original here for reference, in case you need to make another copy. You can accept a case multiple times to get fresh copies.
                    </p>
                    {!isJudicial && (
                    <p style={{
                        fontSize: theme.typography?.fontSize?.sm || '14px',
                        color: theme.colors.text?.primary || '#000000',
                        marginBottom: 0,
                        lineHeight: '1.5',
                    }}>
                        We'll also make a couple of adjustments, setting the attorney on the case and which party you represent.
                    </p>
                    )}
                </div>

                {!isJudicial && <div style={{ marginBottom: '20px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: theme.typography?.fontSize?.sm || '14px',
                        fontWeight: '600',
                        color: theme.colors.text?.primary || '#000000',
                    }}>
                        Which party do you represent?*
                    </label>
                    <div style={{
                        display: 'inline-flex',
                        gap: '0',
                        flexWrap: 'wrap',
                        border: `2px solid ${theme.colors.border?.default || '#e5e7eb'}`,
                        borderRadius: '20px',
                        padding: '2px',
                        backgroundColor: theme.colors.surface || '#ffffff',
                    }}>
                        <button
                            type="button"
                            onClick={() => setRepresentedParty('partyA')}
                            style={{
                                padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                                border: 'none',
                                borderRadius: representedParty === 'partyA' ? '18px' : '0',
                                backgroundColor: representedParty === 'partyA' ? theme.colors.primary : 'transparent',
                                color: representedParty === 'partyA' ? theme.colors.text?.inverse || '#ffffff' : theme.colors.text?.primary || '#000000',
                                fontSize: theme.typography?.fontSize?.sm || '14px',
                                fontWeight: representedParty === 'partyA' ? theme.typography?.fontWeight?.medium : theme.typography?.fontWeight?.normal,
                                cursor: 'pointer',
                                transition: `all ${theme.transitions?.normal || '0.2s'}`,
                                whiteSpace: 'nowrap',
                                minHeight: '36px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {partyAName}
                        </button>
                        <button
                            type="button"
                            onClick={() => setRepresentedParty('partyB')}
                            style={{
                                padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                                border: 'none',
                                borderRadius: representedParty === 'partyB' ? '18px' : '0',
                                backgroundColor: representedParty === 'partyB' ? theme.colors.primary : 'transparent',
                                color: representedParty === 'partyB' ? theme.colors.text?.inverse || '#ffffff' : theme.colors.text?.primary || '#000000',
                                fontSize: theme.typography?.fontSize?.sm || '14px',
                                fontWeight: representedParty === 'partyB' ? theme.typography?.fontWeight?.medium : theme.typography?.fontWeight?.normal,
                                cursor: 'pointer',
                                transition: `all ${theme.transitions?.normal || '0.2s'}`,
                                whiteSpace: 'nowrap',
                                minHeight: '36px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {partyBName}
                        </button>
                    </div>
                </div>}

                {!isJudicial && <div style={{ marginBottom: '20px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: theme.typography?.fontSize?.sm || '14px',
                        fontWeight: '600',
                        color: theme.colors.text?.primary || '#000000',
                    }}>
                        Who is the professional listed on documents for this case?*
                    </label>
                    <Select
                        value={selectedAttorney}
                        onChange={(option) => setSelectedAttorney(option)}
                        options={attorneyOptions}
                        isSearchable={true}
                        placeholder="Select an attorney..."
                        styles={{
                            control: (base) => ({
                                ...base,
                                borderColor: theme.colors.border?.default || '#e5e7eb',
                                '&:hover': {
                                    borderColor: theme.colors.border?.default || '#e5e7eb',
                                },
                            }),
                        }}
                    />
                </div>}

                <div style={{ marginBottom: '20px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: theme.typography?.fontSize?.sm || '14px',
                        fontWeight: '600',
                        color: theme.colors.text?.primary || '#000000',
                    }}>
                        Case Note*
                    </label>
                    <textarea
                        value={caseNote}
                        onChange={(e) => setCaseNote(e.target.value)}
                        placeholder="Enter a case note..."
                        style={{
                            width: '100%',
                            minHeight: '80px',
                            padding: '8px',
                            border: `1px solid ${theme.colors.border?.default || '#e5e7eb'}`,
                            borderRadius: '4px',
                            fontSize: theme.typography?.fontSize?.sm || '14px',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                        }}
                    />
                </div>

                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                }}>
                    <button
                        onClick={onClose}
                        disabled={isAccepting}
                        style={{
                            padding: '8px 16px',
                            border: `1px solid ${theme.colors.border?.default || '#e5e7eb'}`,
                            borderRadius: '4px',
                            backgroundColor: (theme.colors.background as any)?.primary || '#ffffff',
                            color: theme.colors.text?.primary || '#000000',
                            cursor: isAccepting ? 'not-allowed' : 'pointer',
                            fontSize: theme.typography?.fontSize?.sm || '14px',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleAccept}
                        disabled={isAccepting || (!isJudicial && (!selectedAttorney || !representedParty)) || !caseNote.trim()}
                        aria-busy={isAccepting}
                        style={{
                            padding: '8px 16px',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: isAccepting || (!isJudicial && !selectedAttorney) || !caseNote.trim()
                                ? (theme.colors.primary || '#6B46C1') + '80'
                                : theme.colors.primary || '#6B46C1',
                            color: '#ffffff',
                            cursor: isAccepting || (!isJudicial && !selectedAttorney) || !caseNote.trim() ? 'not-allowed' : 'pointer',
                            fontSize: theme.typography?.fontSize?.sm || '14px',
                            fontWeight: '500',
                        }}
                    >
                        {isAccepting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '6px' }} />
                                Accepting...
                            </>
                        ) : 'Accept Case'}
                    </button>
                </div>
            </div>
        </div>
    );
};
